if (!customElements.get('klaviyo-waitlist')) {
  customElements.define(
    'klaviyo-waitlist',
    class KlaviyoWaitlist extends HTMLElement {
      constructor() {
        super();

        this.productInfo = this.closest('product-info');
        this.klaviyoWaitlist = this.productInfo?.querySelector('klaviyo-waitlist');
        this.productForm = this.productInfo?.querySelector('product-form');
        this.waitlistButton = this.querySelector('.pv__waitlist');
        this.waitlistEmail = this.querySelector('.product__klaviyo-email');
        this.disclaimer = this.querySelector('.product__waitlist-disclaimer');

        // Initial state: email HIDDEN (already set in HTML)
        // Button already has 'inactive' class from HTML, no need to add it again
        if (this.waitlistEmail) {
          this.waitlistEmail.style.display = 'none';
        }
        if (this.disclaimer) {
          this.disclaimer.style.display = 'none';
        }

        if (this.waitlistButton) {
          this.waitlistButton.addEventListener('click', this.onButtonClick.bind(this));
        }

        // Listen for email input to activate button
        if (this.waitlistEmail) {
          this.waitlistEmail.addEventListener('input', this.onEmailInput.bind(this));
        }
      }


      onButtonClick() {
        // Re-query elements in case innerHTML was replaced
        this.waitlistButton = this.querySelector('.pv__waitlist');
        this.waitlistEmail = this.querySelector('.product__klaviyo-email');
        this.disclaimer = this.querySelector('.product__waitlist-disclaimer');

        // Check if customer is logged in (has email in data attribute)
        const customerEmail = this.waitlistButton?.dataset?.customerEmail;
        const isLoggedIn = customerEmail && customerEmail.length > 0;

        // If user is logged in, submit directly without showing email input
        if (isLoggedIn) {
          // Set email value from customer data
          if (this.waitlistEmail) {
            this.waitlistEmail.value = customerEmail;
          }

          // Find the currently selected waitlist size and add 'waitlist-button-clicked' class
          const selectedWaitlistSize = this.productInfo?.querySelector('.js-product-swatch.waitlist:checked');
          if (selectedWaitlistSize) {
            selectedWaitlistSize.classList.add('waitlist-button-clicked');
          }

          // Submit immediately
          this.onSubmitHandler();
          return;
        }

        // For non-logged-in users: check if email is currently hidden
        const emailIsHidden = this.waitlistEmail && (
          this.waitlistEmail.style.display === 'none' ||
          window.getComputedStyle(this.waitlistEmail).display === 'none'
        );

        // If email is hidden, show it and make button inactive (first click)
        if (emailIsHidden) {
          // Find the currently selected waitlist size and add 'waitlist-button-clicked' class
          const selectedWaitlistSize = this.productInfo?.querySelector('.js-product-swatch.waitlist:checked');
          if (selectedWaitlistSize) {
            selectedWaitlistSize.classList.add('waitlist-button-clicked');
          }

          // Show email field and disclaimer
          if (this.waitlistEmail) {
            this.waitlistEmail.style.display = 'block';
          }
          if (this.disclaimer) {
            this.disclaimer.style.display = 'block';
          }
          // Make button grey/inactive
          if (this.waitlistButton) {
            this.waitlistButton.classList.remove('active');
            this.waitlistButton.classList.add('inactive');
          }
          if (this.waitlistEmail) {
            this.waitlistEmail.focus();
          }
          return;
        }

        // If email is empty, don't submit
        if (!this.waitlistEmail || this.waitlistEmail.value.length === 0) {
          if (this.waitlistEmail) {
            this.waitlistEmail.focus();
          }
          return;
        }

        // Submit
        this.onSubmitHandler();
      }

      onEmailInput() {
        // Re-query elements in case innerHTML was replaced
        this.waitlistButton = this.querySelector('.pv__waitlist');
        this.waitlistEmail = this.querySelector('.product__klaviyo-email');

        // When user starts typing, activate button
        if (this.waitlistEmail && this.waitlistEmail.value.length > 0) {
          if (this.waitlistButton) {
            this.waitlistButton.classList.remove('inactive');
            this.waitlistButton.classList.add('active');
          }
        } else {
          if (this.waitlistButton) {
            this.waitlistButton.classList.add('inactive');
            this.waitlistButton.classList.remove('active');
          }
        }
      }

      onSizeSelected() {
        // Called when a waitlistable size is selected
        // Make button active (black)
        // Re-query for button in case innerHTML was replaced
        this.waitlistButton = this.querySelector('.pv__waitlist');
        if (!this.waitlistButton) return;

        this.waitlistButton.classList.remove('inactive');
        this.waitlistButton.classList.add('active');
      }

      onSubmitHandler() {
        // check validity of email
        if (!this.waitlistEmail.checkValidity()) {
          this.waitlistEmail.reportValidity();
          return;
        }

        // Show loading state
        this.showLoadingState();

        this.waitlistButton = this.querySelector('.pv__waitlist');
        this.handleKlaviyoSubmit(this.productInfo, this.waitlistButton);
      }

      showLoadingState() {
        // Re-query button in case innerHTML was replaced
        this.waitlistButton = this.querySelector('.pv__waitlist');
        if (!this.waitlistButton) return;

        // Change button to show "ADDING..."
        this.waitlistButton.classList.remove('active');
        this.waitlistButton.classList.add('loading');
        const buttonText = this.waitlistButton.querySelector('.pv__waitlist-text:not(.pv__waitlist-text--success)');
        if (buttonText) {
          buttonText.textContent = 'ADDING...';
        }
      }

      showSuccessState() {
        // Re-query elements in case innerHTML was replaced
        this.waitlistButton = this.querySelector('.pv__waitlist');
        this.waitlistEmail = this.querySelector('.product__klaviyo-email');

        if (this.waitlistButton) {
          // Add success class to show green background and check icon
          this.waitlistButton.classList.remove('loading', 'active', 'inactive');
          this.waitlistButton.classList.add('success');
        }
        // Hide email field on success
        if (this.waitlistEmail) {
          this.waitlistEmail.style.display = 'none';
        }
      }

      toggleWaitlistButton(waitlist = true) {
        if (waitlist) {
          this.klaviyoWaitlist.classList.remove('hide');
          this.productForm.classList.add('hide');
        } else {
          this.klaviyoWaitlist.classList.add('hide');
          this.productForm.classList.remove('hide');
        }
      }

      async handleKlaviyoSubmit($productInfo) {
        const email = $productInfo.querySelector('.product__klaviyo-email')?.value;
        if (!email) {
          return;
        }

        const waitlistButton = $productInfo.querySelector('.pv__waitlist');
        if (!waitlistButton) {
          console.error('[Klaviyo Waitlist] Waitlist button not found');
          return;
        }

        const payload = {
          ProductName: waitlistButton.dataset.productName || '',
          ProductID: waitlistButton.dataset.productId || '',
          SKU: waitlistButton.dataset.sku || '',
          Categories: waitlistButton.dataset.categories || '',
          ImageURL: waitlistButton.dataset.imageUrl || '',
          URL: waitlistButton.dataset.url || window.location.href,
          Brand: waitlistButton.dataset.brand || 'Everlane',
          Price: waitlistButton.dataset.price || '',
          CompareAtPrice: waitlistButton.dataset.compareAtPrice || '',
          VariantName: waitlistButton.dataset.variantName || '',
        };

        klaviyo.identify({ email });

        klaviyo.track(
          'Shopify Waitlist',
          payload,
        )

        this.showSuccessState();
      }
    },
  );
}
